import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
  useMediaQuery,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { AuditStatus, AssignmentStatus } from '../../../pages/Compliance/Enums/complianceEnums';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { trainingClient } from '../../../app/services/api/compliance/training';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { wrapReportHtml } from '../../../utils/trainingReportHtml';
import { AuditReportFilter } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { Section } from '../../../components/ui/Section';
import { getDisplayStoreNumberByInternal } from '../../../utils/storeNumber';


enum ReportType {
  AuditSummary = 'AuditSummary',
  AuditDetails = 'AuditDetails',
  TrainingSummary = 'TrainingSummary',
  TrainingCompletion = 'TrainingCompletion',
  ComplianceOverview = 'ComplianceOverview',
  ActionItemsSummary = 'ActionItemsSummary',
}

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatNotesForHtml = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  return escapeHtml(value).replace(/\r?\n/g, '<br/>');
};

interface ReportFilter {
  reportType: ReportType;
  startDate: string;
  endDate: string;
  storeIds: number[];
  auditStatuses: AuditStatus[];
  assignmentStatuses: AssignmentStatus[];
}

interface GeneratedReport {
  id: number;
  reportType: ReportType;
  generatedDate: Date;
  parameters: string;
  fileUrl: string;
}

const ComplianceReports: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { logError } = useLogError();
  const { selectedFranchise, stores } = useContext(UserContext);
  const { t } = useTranslation('complianceAudits');

  // State
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [recentReports, setRecentReports] = useState<GeneratedReport[]>([]);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<string | null>(null);
  const localeLanguage = navigator.languages?.[0] || navigator.language;
  // Filter state
  const [filter, setFilter] = useState<ReportFilter>({
    reportType: ReportType.ComplianceOverview,
    startDate: format(new Date(new Date().setMonth(new Date().getMonth() - 1)), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    storeIds: [],
    auditStatuses: [],
    assignmentStatuses: [],
  });

  useEffect(() => {
    loadRecentReports();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadRecentReports = async () => {
    try {
      setLoading(true);
      setRecentReports([]);
    } catch (err) {
      console.error('Error loading recent reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const getReportTypeDescription = (reportType: ReportType): string => {
    switch (reportType) {
      case ReportType.AuditSummary:
        return t('reports.auditSummaryDescription');
      case ReportType.AuditDetails:
        return t('reports.auditDetailDescription');
      case ReportType.TrainingSummary:
        return t('reports.trainingOverviewDescription');
      case ReportType.TrainingCompletion:
        return t('reports.trainingDetailDescription');
      case ReportType.ComplianceOverview:
        return t('reports.complianceOverviewDescription');
      case ReportType.ActionItemsSummary:
        return t('reports.actionItemSummaryDescription');
      default:
        return '';
    }
  };

  // Convert string enum AssignmentStatus to numeric (backend expects: Assigned=0, InProgress=1, Completed=2, Failed=3)
  const convertAssignmentStatusToNumber = (status: AssignmentStatus): number => {
    switch (status) {
      case AssignmentStatus.Assigned:
        return 0;
      case AssignmentStatus.InProgress:
        return 1;
      case AssignmentStatus.Completed:
        return 2;
      case AssignmentStatus.Expired: // Map Expired to Failed
        return 3;
      default:
        return 0;
    }
  };

  const handleGenerateReport = async () => {
    // Validation
    if (!filter.startDate || !filter.endDate) {
      setError(t('reports.selectDates'));
      return;
    }

    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      setError(t('reports.startBeforeEnd'));
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      // Training reports use the training client
      if (filter.reportType === ReportType.TrainingSummary ||
        filter.reportType === ReportType.TrainingCompletion) {
        const reportFilter = {
          startDate: filter.startDate,
          endDate: filter.endDate,
          franchiseId: selectedFranchise?.franchiseId,
          assignmentStatuses: filter.assignmentStatuses.length > 0
            ? filter.assignmentStatuses.map(s => convertAssignmentStatusToNumber(s))
            : undefined,
        };

        const blob = filter.reportType === ReportType.TrainingSummary
          ? await trainingClient.exportSummaryReport(reportFilter)
          : await trainingClient.exportCompletionReport(reportFilter);

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const reportName = filter.reportType === ReportType.TrainingSummary ? 'training-summary' : 'training-completion';
        link.download = `${reportName}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Audit reports use the compliance reports API
        const auditFilter: AuditReportFilter = {
          startDate: filter.startDate,
          endDate: filter.endDate,
          auditStatuses: filter.auditStatuses.length > 0
            ? filter.auditStatuses.map(s => s as number)
            : undefined,
        };

        let blob: Blob;
        let reportName: string;

        switch (filter.reportType) {
          case ReportType.AuditSummary:
            blob = await complianceService.reports.auditSummary.export(auditFilter);
            reportName = 'audit-summary';
            break;
          case ReportType.AuditDetails:
            blob = await complianceService.reports.auditDetails.export(auditFilter);
            reportName = 'audit-details';
            break;
          case ReportType.ActionItemsSummary:
            blob = await complianceService.reports.actionItems.export(auditFilter);
            reportName = 'action-items-summary';
            break;
          case ReportType.ComplianceOverview:
            blob = await complianceService.reports.complianceOverview.export(auditFilter);
            reportName = 'compliance-overview';
            break;
          default:
            throw new Error('Unknown report type');
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${reportName}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      setSuccess(t('reports.reportGeneratedSuccess'));
      loadRecentReports();
    } catch (err) {
      console.error('Error generating report:', err);
      logError(err);
      setError(err instanceof Error ? err.message : t('reports.reportFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewReport = async () => {
    try {
      setGenerating(true);
      setError(null);

      // Training reports use HTML preview via training client
      if (filter.reportType === ReportType.TrainingSummary ||
        filter.reportType === ReportType.TrainingCompletion) {
        const reportFilter = {
          startDate: filter.startDate,
          endDate: filter.endDate,
          franchiseId: selectedFranchise?.franchiseId,
          assignmentStatuses: filter.assignmentStatuses.length > 0
            ? filter.assignmentStatuses.map(s => convertAssignmentStatusToNumber(s))
            : undefined,
        };

        const htmlContent = filter.reportType === ReportType.TrainingSummary
          ? await trainingClient.previewSummaryReport(reportFilter)
          : await trainingClient.previewCompletionReport(reportFilter);

        setPreviewData(htmlContent);
        setPreviewDialogOpen(true);
      } else {
        // Audit reports: fetch preview data and render as HTML table
        const auditFilter: AuditReportFilter = {
          startDate: filter.startDate,
          endDate: filter.endDate,
          auditStatuses: filter.auditStatuses.length > 0
            ? filter.auditStatuses.map(s => s as number)
            : undefined,
        };

        let htmlContent = '';

        switch (filter.reportType) {
          case ReportType.AuditSummary: {
            const report = await complianceService.reports.auditSummary.preview(auditFilter);
            const auditSummaryBody = `
              <h2>${t('reports.auditSummary')}</h2>
              <table class='summary-table'>
                <thead><tr><th>${t('reports.metric')}</th><th>${t('reports.value')}</th></tr></thead>
                <tbody>
                  <tr><td class='metric'>${t('reports.totalAudits')}</td><td class='value'>${report.totalAudits}</td></tr>
                  <tr><td class='metric'>${t('reports.completedAudits')}</td><td class='value'>${report.completedAudits}</td></tr>
                  <tr><td class='metric'>${t('reports.averageScore')}</td><td class='value'>${report.averageScore?.toFixed(1) ?? '—'}</td></tr>
                  <tr><td class='metric'>${t('reports.complianceRate')}</td><td class='value'>${report.complianceRate?.toFixed(1) ?? '—'}%</td></tr>
                </tbody>
              </table>
              ${report.byStore?.length ? `
                <h2>${t('reports.byStore')}</h2>
                <table class='detail-table'>
                  <thead><tr><th>${t('common.store')}</th><th>${t('reports.auditCount')}</th><th>${t('reports.averageScore')}</th></tr></thead>
                  <tbody>${report.byStore.map(s => `<tr><td><strong>${s.storeName} (${getDisplayStoreNumberByInternal(stores, s.storeNumber)})</strong></td><td>${s.auditCount}</td><td>${s.averageScore?.toFixed(1) ?? '—'}</td></tr>`).join('')}</tbody>
                </table>` : ''}
            `;
            htmlContent = wrapReportHtml(
              t('reports.auditSummary'),
              `${auditFilter.startDate} — ${auditFilter.endDate}`,
              auditSummaryBody,
            );
            break;
          }
          case ReportType.AuditDetails: {
            const report = await complianceService.reports.auditDetails.preview(auditFilter);
            const auditDetailsBody = `
              <h2>${t('reports.auditDetail')}</h2>
              <p>${t('reports.period')}: ${auditFilter.startDate} — ${auditFilter.endDate}</p>
              <table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse; width:100%">
                <tr><th>ID</th><th>${t('common.type')}</th><th>${t('common.store')}</th><th>${t('common.status')}</th><th>${t('reports.score')}</th><th>${t('reports.scheduledDate')}</th><th>${t('reports.completedDate')}</th></tr>
                ${report.audits?.map(a => `<tr><td>${a.id}</td><td>${a.auditTypeName}</td><td>${a.storeName} (${getDisplayStoreNumberByInternal(stores, a.storeNumber)})</td><td>${a.status}</td><td>${a.score ?? '-'}</td><td>${a.scheduledDate ? new Date(a.scheduledDate).toLocaleString() : '-'}</td><td>${a.completedDate ? new Date(a.completedDate).toLocaleString() : '-'}</td></tr>`).join('') ?? ''}
              </table>
            `;
            htmlContent = wrapReportHtml(
              t('reports.auditDetail'),
              `${auditFilter.startDate} — ${auditFilter.endDate}`,
              auditDetailsBody,
            );
            break;
          }
          case ReportType.ActionItemsSummary: {
            const report = await complianceService.reports.actionItems.preview(auditFilter);
            const hasAuditorNotes = report.actionItemDetails?.some(ai => ai.auditorNotes != null) ?? false;
            const actionItemsBody = `
              <h2>${t('reports.actionItemSummary')}</h2>
              <table class='summary-table'>
                <thead><tr><th>${t('reports.metric')}</th><th>${t('reports.value')}</th></tr></thead>
                <tbody>
                  <tr><td class='metric'>${t('reports.totalItems')}</td><td class='value'>${report.totalItems}</td></tr>
                  <tr><td class='metric'>${t('reports.openItems')}</td><td class='value'>${report.openItems}</td></tr>
                  <tr><td class='metric'>${t('reports.resolvedItems')}</td><td class='value'>${report.resolvedItems}</td></tr>
                  <tr><td class='metric'>${t('reports.overdueItems')}</td><td class='value'>${report.overdueItems}</td></tr>
                </tbody>
              </table>
              ${report.actionItemDetails?.length ? `
                <h2>${t('reports.actionItemDetails')}</h2>
                <table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse; width:100%">
                  <tr><th>${t('actionItems.title')}</th><th>${t('actionItems.priority')}</th><th>${t('common.status')}</th><th>${t('common.store')}</th><th>${t('actionItems.assignedTo')}</th><th>${t('actionItems.dueDate')}</th><th>${t('actionItemDetail.resolvedDate')}</th><th>${t('actionItems.resolutionNotes')}</th><th>${t('actionItemDetail.closedDate')}</th><th>${t('actionItemDetail.closeNotes')}</th>${hasAuditorNotes ? `<th>${t('actionItemDetail.auditorNotes')}</th>` : ''}</tr>
                  ${report.actionItemDetails.map(ai => `<tr><td>${ai.title}</td><td>${ai.priority}</td><td>${ai.status}</td><td>${ai.storeName} (${getDisplayStoreNumberByInternal(stores, ai.storeNumber)})</td><td>${ai.assignedToUserName}</td><td>${ai.dueDate ? new Date(ai.dueDate).toLocaleDateString() : '-'}</td><td>${ai.resolvedDate ? new Date(ai.resolvedDate).toLocaleDateString() : '-'}</td><td>${formatNotesForHtml(ai.resolutionNotes)}</td><td>${ai.closedDate ? new Date(ai.closedDate).toLocaleDateString() : '-'}</td><td>${formatNotesForHtml(ai.closeNotes)}</td>${hasAuditorNotes ? `<td>${formatNotesForHtml(ai.auditorNotes)}</td>` : ''}</tr>`).join('')}
                </table>` : ''}
            `;
            htmlContent = wrapReportHtml(
              t('reports.actionItemSummary'),
              `${auditFilter.startDate} — ${auditFilter.endDate}`,
              actionItemsBody,
            );
            break;
          }
          case ReportType.ComplianceOverview: {
            const report = await complianceService.reports.complianceOverview.preview(auditFilter);
            const overviewBody = `
              <h2>${t('reports.auditSummary')}</h2>
              <table class='summary-table'>
                <thead><tr><th>${t('reports.metric')}</th><th>${t('reports.value')}</th></tr></thead>
                <tbody>
                  <tr><td class='metric'>${t('reports.totalAudits')}</td><td class='value'>${report.auditSummary?.totalAudits ?? 0}</td></tr>
                  <tr><td class='metric'>${t('reports.completedAudits')}</td><td class='value'>${report.auditSummary?.completedAudits ?? 0}</td></tr>
                  <tr><td class='metric'>${t('reports.averageScore')}</td><td class='value'>${report.auditSummary?.averageScore?.toFixed(1) ?? '—'}</td></tr>
                  <tr><td class='metric'>${t('reports.complianceRate')}</td><td class='value'>${report.auditSummary?.complianceRate?.toFixed(1) ?? '—'}%</td></tr>
                </tbody>
              </table>
              <h2>${t('reports.actionItemSummary')}</h2>
              <table class='summary-table'>
                <thead><tr><th>${t('reports.metric')}</th><th>${t('reports.value')}</th></tr></thead>
                <tbody>
                  <tr><td class='metric'>${t('reports.totalItems')}</td><td class='value'>${report.actionItemsSummary?.totalItems ?? 0}</td></tr>
                  <tr><td class='metric'>${t('reports.openItems')}</td><td class='value'>${report.actionItemsSummary?.openItems ?? 0}</td></tr>
                  <tr><td class='metric'>${t('reports.resolvedItems')}</td><td class='value'>${report.actionItemsSummary?.resolvedItems ?? 0}</td></tr>
                  <tr><td class='metric'>${t('reports.overdueItems')}</td><td class='value'>${report.actionItemsSummary?.overdueItems ?? 0}</td></tr>
                </tbody>
              </table>
            `;
            htmlContent = wrapReportHtml(
              t('reports.complianceOverview'),
              `${auditFilter.startDate} — ${auditFilter.endDate}`,
              overviewBody,
            );
            break;
          }
          default:
            throw new Error('Unknown report type');
        }

        setPreviewData(htmlContent);
        setPreviewDialogOpen(true);
      }
    } catch (err) {
      console.error('Error previewing report:', err);
      logError(err);
      setError(err instanceof Error ? err.message : t('reports.previewFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadReport = (report: GeneratedReport) => {
    window.open(report.fileUrl, '_blank');
  };

  const handlePrintPreview = () => {
    if (!previewData) return;

    // Create a new window with the preview content
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Preview</title>
            <style>
              @page { 
                margin: 15mm;
                size: A4 landscape;
              }
              body { 
                margin: 0;
                padding: 15px;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              }
              @media print {
                body { margin: 0; padding: 0; }
              }
            </style>
          </head>
          <body>
            ${previewData}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();

      // Trigger print dialog after content loads
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const handleAuditStatusToggle = (status: AuditStatus) => {
    if (filter.auditStatuses.includes(status)) {
      setFilter({
        ...filter,
        auditStatuses: filter.auditStatuses.filter((s) => s !== status),
      });
    } else {
      setFilter({
        ...filter,
        auditStatuses: [...filter.auditStatuses, status],
      });
    }
  };

  const handleAssignmentStatusToggle = (status: AssignmentStatus) => {
    if (filter.assignmentStatuses.includes(status)) {
      setFilter({
        ...filter,
        assignmentStatuses: filter.assignmentStatuses.filter((s) => s !== status),
      });
    } else {
      setFilter({
        ...filter,
        assignmentStatuses: [...filter.assignmentStatuses, status],
      });
    }
  };

  return (
    <PageArea>
      {/* Header */}
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 6 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PageTitle subTitle={t('reports.pageSubTitle')}>
            {t('reports.pageTitle')}
          </PageTitle>
        </Box>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 4 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 4 }}
        >
          {success}
        </Alert>
      )}

      <Grid container
        spacing={6}>
        {/* Report Configuration */}
        <Grid item
          xs={12}
          md={8}>
          <Section style={{ p: 5, mb: 5 }}>
            <Typography variant="textLG"
              sx={{ fontWeight: 600, mb: 4 }}>
              {t('reports.generateReport')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, mt: 5 }}>
              {/* Report Type */}
              <FormControl fullWidth
                required>
                <InputLabel>{t('reports.reportType')}</InputLabel>
                <Select
                  value={filter.reportType}
                  onChange={(e) => setFilter({ ...filter, reportType: e.target.value as ReportType })}
                  label={t('reports.reportType')}
                >
                  {Object.values(ReportType).map((type) => {
                    const labelMap: Record<ReportType, string> = {
                      [ReportType.AuditSummary]: t('reports.auditSummary'),
                      [ReportType.AuditDetails]: t('reports.auditDetail'),
                      [ReportType.TrainingSummary]: t('reports.trainingOverview'),
                      [ReportType.TrainingCompletion]: t('reports.trainingDetail'),
                      [ReportType.ComplianceOverview]: t('reports.complianceOverview'),
                      [ReportType.ActionItemsSummary]: t('reports.actionItemSummary'),
                    };
                    return (
                      <MenuItem key={type}
                        value={type}>
                        {labelMap[type]}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Typography variant="textSM"
                  color="text.secondary"
                  sx={{ mt: 1.5 }}>
                  {getReportTypeDescription(filter.reportType)}
                </Typography>
              </FormControl>

              {/* Date Range */}
              <Box gap={5} >
                <Typography variant="textMD"
                  sx={{ fontWeight: 600 }}>
                  {t('reports.dateRange')}
                </Typography>
                <Grid container
                  spacing={5}
                  sx={{ mt: 3 }}>
                  <Grid item
                    xs={6} >
                    <DatePickerLocalized
                      useCalendar={false}
                      label={'Start Date'}
                      value={filter.startDate}
                      translations={{
                        clearButtonLabel: t('calendar.clearButtonLabel'),
                        todayButtonLabel: t('calendar.todayButtonLabel'),
                        cancelButtonLabel: t('calendar.cancelButtonLabel'),
                        okButtonLabel: t('calendar.okButtonLabel'),
                      }}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFilter({ ...filter, startDate: newValue });
                        }
                      }}
                    />
                  </Grid>
                  <Grid item
                    xs={6} >
                    <DatePickerLocalized
                      useCalendar={false}
                      label={'End Date'}
                      value={filter.endDate}
                      translations={{
                        clearButtonLabel: t('calendar.clearButtonLabel'),
                        todayButtonLabel: t('calendar.todayButtonLabel'),
                        cancelButtonLabel: t('calendar.cancelButtonLabel'),
                        okButtonLabel: t('calendar.okButtonLabel'),
                      }}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFilter({ ...filter, endDate: newValue });
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Audit Status Filter (for audit reports) */}
              {(filter.reportType === ReportType.AuditSummary ||
                filter.reportType === ReportType.AuditDetails ||
                filter.reportType === ReportType.ComplianceOverview) &&
                (
                  <Box>
                    <Typography variant="textMD"
                      sx={{ fontWeight: 600, mb: 3 }}>
                      {t('reports.auditStatusFilter')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4, mb: 4 }}>
                      {Object.keys(AuditStatus)
                        .filter(k => isNaN(Number(k)))
                        .map((status) => (
                          <Chip
                            key={status}
                            label={status}
                            onClick={() => handleAuditStatusToggle(AuditStatus[status as keyof typeof AuditStatus])}
                            color={filter.auditStatuses.includes(AuditStatus[status as keyof typeof AuditStatus]) ? 'primary' : 'default'}
                            variant={filter.auditStatuses.includes(AuditStatus[status as keyof typeof AuditStatus]) ? 'filled' : 'outlined'}
                          />
                        ))}
                    </Box>
                    <Typography variant="textSM"
                      color="text.secondary"
                      sx={{ mt: 5 }}>
                      {t('reports.leaveEmptyForAll')}
                    </Typography>
                  </Box>
                )}

              {/* Training Status Filter (for training reports) */}
              {(filter.reportType === ReportType.TrainingSummary ||
                filter.reportType === ReportType.TrainingCompletion ||
                filter.reportType === ReportType.ComplianceOverview) &&
                (
                  <Box>
                    <Typography variant="textMD"
                      sx={{ fontWeight: 600, mb: 3 }}>
                      {t('reports.trainingStatusFilter')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4, mb: 4 }}>
                      {Object.keys(AssignmentStatus)
                        .filter(k => isNaN(Number(k)))
                        .map((status) => (
                          <Chip
                            key={status}
                            label={status}
                            onClick={() => handleAssignmentStatusToggle(AssignmentStatus[status as keyof typeof AssignmentStatus])}
                            color={filter.assignmentStatuses.includes(AssignmentStatus[status as keyof typeof AssignmentStatus]) ? 'primary' : 'default'}
                            variant={filter.assignmentStatuses.includes(AssignmentStatus[status as keyof typeof AssignmentStatus]) ? 'filled' : 'outlined'}
                          />
                        ))}
                    </Box>
                    <Typography variant="textSM"
                      color="text.secondary"
                      sx={{ mt: 1.5 }}>
                      {t('reports.leaveEmptyForAll')}
                    </Typography>
                  </Box>
                )}

              <Divider sx={{ my: 2 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                <Button
                  variant="secondary"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreviewReport}
                  disabled={generating}
                >
                  {t('common.preview')}
                </Button>
                <Button
                  variant="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleGenerateReport}
                  disabled={generating}
                >
                  {generating ? t('reports.generating') : t('reports.generateAndDownload')}
                </Button>
              </Box>
            </Box>
          </Section>
        </Grid>

        {/* Report Templates */}
        <Grid item
          xs={12}
          md={4}>


          <Section style={{ p: 5, mb: 5 }}>
            <Typography variant="textLG"
              sx={{ fontWeight: 600, mb: 5 }}>
              {t('reports.quickTemplates')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, mt: 5 }}>
              <Button
                variant="secondary"
                fullWidth
                onClick={() =>
                  setFilter({
                    ...filter,
                    reportType: ReportType.ComplianceOverview,
                    startDate: format(new Date(new Date().setDate(new Date().getDate() - 7)), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd'),
                  })
                }
              >
                {t('reports.last7Days')}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() =>
                  setFilter({
                    ...filter,
                    reportType: ReportType.AuditSummary,
                    startDate: format(new Date(new Date().setMonth(new Date().getMonth() - 1)), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd'),
                  })
                }
              >
                {t('reports.monthlyAuditSummary')}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() =>
                  setFilter({
                    ...filter,
                    reportType: ReportType.TrainingCompletion,
                    startDate: format(new Date(new Date().setMonth(new Date().getMonth() - 3)), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd'),
                  })
                }
              >
                {t('reports.quarterlyTrainingReport')}
              </Button>
            </Box>
          </Section>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            [theme.breakpoints.down('md')]: {
              margin: 1,
              maxHeight: 'calc(100% - 16px)',
            },
          },
        }}
      >
        <DialogTitle>{t('reports.reportPreview')}</DialogTitle>
        <DialogContent>
          {previewData ? (
            <Box
              sx={{
                p: 2,
                [theme.breakpoints.down('md')]: {
                  p: 1,
                  overflowX: 'auto',
                  '& table': {
                    fontSize: '0.75rem',
                    minWidth: 'max-content',
                  },
                  '& th, & td': {
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
              dangerouslySetInnerHTML={{ __html: previewData }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="textMD">
                {t('reports.loadingPreview')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'space-between',
          [theme.breakpoints.down('md')]: {
            flexDirection: 'column-reverse',
            gap: 1.5,
            '& button': {
              width: '100%',
            },
          },
        }}>
          <Button
            variant="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrintPreview}
            disabled={!previewData}
          >
            {t('common.print')}
          </Button>
          <Button variant="secondary"
            onClick={() => setPreviewDialogOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea >
  );
};

export default ComplianceReports;
